import { useState } from "react";
import { motion } from "framer-motion";
import { CARD, CARD2, CARD3, BD, TX, TX2, GR, BL, YL, RD } from "../../utils/constants";
import { scoreColor, biasColor, getInvestmentAdvice, sanitizeDecimalInput,
         NA, isNum, fmtPrice, fmtPct, fmtDate,
         fmtMoney, fmtMoneySigned, fmtShares } from "../../utils/formatUtils";
import { buildTradeRow, computeTradeStats } from "../../utils/tradeStats";

/** One statistic. Renders N/A rather than a misleading 0 when not computable. */
function Stat({ label, value, color }) {
  const shown = value === null || value === undefined ? NA : value;
  return (
    <div style={{ background: CARD3, borderRadius: 6, padding: "7px 10px" }}>
      <div style={{ fontSize: 7, color: TX2, letterSpacing: 1, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: shown === NA ? TX2 : (color || TX) }}>{shown}</div>
    </div>
  );
}

/**
 * Performance summary across tracked trades.
 * Win rate and averages come from CLOSED trades only — counting open positions
 * would let the numbers drift with intraday noise and flatter unfinished trades.
 */
function TradeStatsPanel({ stats }) {
  const pnlColor = (v) => (!isNum(v) ? TX2 : v >= 0 ? GR : RD);
  return (
    <div className="ts-panel" style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
        <span style={{ fontSize: 9, color: TX2, letterSpacing: 2, fontWeight: 600 }}>PERFORMANCE</span>
        <span style={{ fontSize: 7, color: TX2 }}>
          {stats.openCount} open · {stats.closedCount} closed
        </span>
      </div>
      {/* Cash summary.
          Shown only once at least one position has an amount recorded, and it
          says how many it covers — a portfolio where some trades predate this
          would otherwise show a total that silently omits them. */}
      {stats.trackedWithAmount > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(104px,1fr))", gap: 7, marginBottom: 9 }}>
          <Stat label="INVESTED (OPEN)" value={isNum(stats.openInvested) ? fmtMoney(stats.openInvested) : null} color={TX} />
          <Stat label="VALUE NOW"       value={isNum(stats.openValue) ? fmtMoney(stats.openValue) : null} color={TX} />
          <Stat label="OPEN P/L"        value={isNum(stats.unrealizedPnl) ? fmtMoneySigned(stats.unrealizedPnl) : null} color={pnlColor(stats.unrealizedPnl)} />
          <Stat label="REALIZED P/L"    value={isNum(stats.realizedPnl) ? fmtMoneySigned(stats.realizedPnl) : null} color={pnlColor(stats.realizedPnl)} />
          <Stat label="TOTAL P/L"       value={isNum(stats.totalPnl) ? fmtMoneySigned(stats.totalPnl) : null} color={pnlColor(stats.totalPnl)} />
        </div>
      )}
      {stats.trackedWithAmount > 0 && stats.trackedWithAmount < stats.openCount + stats.closedCount && (
        <div style={{ fontSize: 7, color: TX2, marginBottom: 9 }}>
          Cash figures cover {stats.trackedWithAmount} of {stats.openCount + stats.closedCount} tracked positions — the rest have no invested amount recorded.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(96px,1fr))", gap: 7 }}>
        <Stat label="WIN RATE"      value={isNum(stats.winRate) ? `${stats.winRate.toFixed(0)}%` : null} color={YL} />
        <Stat label="AVG RETURN"    value={isNum(stats.avgReturn) ? fmtPct(stats.avgReturn, 1) : null} color={pnlColor(stats.avgReturn)} />
        <Stat label="AVG WINNER"    value={isNum(stats.avgWinner) ? fmtPct(stats.avgWinner, 1) : null} color={GR} />
        <Stat label="AVG LOSER"     value={isNum(stats.avgLoser) ? fmtPct(stats.avgLoser, 1) : null} color={RD} />
        <Stat label="BEST TRADE"    value={stats.bestTrade ? `${stats.bestTrade.symbol} ${fmtPct(stats.bestTrade.realizedPct, 1)}` : null} color={GR} />
        <Stat label="WORST TRADE"   value={stats.worstTrade ? `${stats.worstTrade.symbol} ${fmtPct(stats.worstTrade.realizedPct, 1)}` : null} color={RD} />
        <Stat label="OPEN AVG P/L"  value={isNum(stats.avgUnrealizedPct) ? fmtPct(stats.avgUnrealizedPct, 1) : null} color={pnlColor(stats.avgUnrealizedPct)} />
      </div>
      {stats.closedCount === 0 && (
        <div style={{ fontSize: 7, color: TX2, marginTop: 7 }}>
          Win rate and averages appear once you close a trade — they are measured on completed trades only.
        </div>
      )}
    </div>
  );
}

/** Records an exit price, turning an open position into a completed trade. */
function CloseTrade({ symbol, onClose }) {
  const [price, setPrice] = useState("");
  return (
    <div style={{ display: "flex", gap: 5, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
      <input
        type="text"
        inputMode="decimal"
        value={price}
        onChange={(e) => setPrice(sanitizeDecimalInput(e.target.value))}
        placeholder="exit $"
        style={{ width: 66, background: CARD3, border: `1px solid ${BD}`, borderRadius: 5, padding: "4px 6px", color: TX, fontSize: 9 }}
      />
      <button
        className="btn"
        onClick={() => { if (price) { onClose(symbol, price); setPrice(""); } }}
        style={{ background: YL + "18", border: `1px solid ${YL}40`, borderRadius: 5, padding: "4px 8px", color: YL, fontSize: 8, fontWeight: 700 }}
      >
        Close
      </button>
    </div>
  );
}

function QuickTrack({ symbol, onAdd }) {
  const [price, setPrice] = useState("");
  // Optional: entry price alone still tracks the trade, it just can't report
  // profit and loss in dollars.
  const [amount, setAmount] = useState("");
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
      <input
        type="text"
        inputMode="decimal"
        value={price}
        onChange={(e) => setPrice(sanitizeDecimalInput(e.target.value))}
        placeholder="entry $"
        style={{ width: 70, background: CARD3, border: `1px solid ${BD}`, borderRadius: 5, padding: "5px 7px", color: TX, fontSize: 9 }}
      />
      <input
        type="text"
        inputMode="decimal"
        value={amount}
        onChange={(e) => setAmount(sanitizeDecimalInput(e.target.value))}
        placeholder="invested $"
        title="How much money you put in — optional, enables dollar profit and loss"
        style={{ width: 78, background: CARD3, border: `1px solid ${BD}`, borderRadius: 5, padding: "5px 7px", color: TX, fontSize: 9 }}
      />
      <button
        className="btn"
        onClick={() => {
          if (price) {
            onAdd(symbol, price, { amountInvested: Number(amount) });
            setPrice("");
            setAmount("");
          }
        }}
        style={{ background: BL + "18", border: `1px solid ${BL}40`, borderRadius: 5, padding: "5px 9px", color: BL, fontSize: 8, fontWeight: 700 }}
      >
        Track
      </button>
    </div>
  );
}

/**
 * Sets or corrects how much money a tracked position holds.
 *
 * The amount could previously only be entered while first adding a position,
 * so a trade tracked before the field existed — or one where the number was
 * mistyped — could not be fixed without deleting and re-adding it, which threw
 * away the entry date and the scanner's view at entry.
 */
function EditAmount({ symbol, current, onSave }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");

  if (!open) {
    return (
      <span
        onClick={(e) => { e.stopPropagation(); setValue(isNum(current) ? String(current) : ""); setOpen(true); }}
        style={{ fontSize: 7, color: BL, cursor: "pointer", textDecoration: "underline", whiteSpace: "nowrap" }}
        title="Set how much money you put into this position"
      >
        {isNum(current) ? "edit amount" : "+ add amount"}
      </span>
    );
  }

  const commit = () => {
    if (value) onSave(symbol, value);
    setOpen(false);
  };

  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
      <input
        autoFocus
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(sanitizeDecimalInput(e.target.value))}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setOpen(false); }}
        placeholder="invested $"
        style={{ width: 72, background: CARD3, border: `1px solid ${BD}`, borderRadius: 4, padding: "3px 5px", color: TX, fontSize: 9 }}
      />
      <button className="btn" onClick={commit}
        style={{ background: BL + "18", border: `1px solid ${BL}40`, borderRadius: 4, padding: "3px 7px", color: BL, fontSize: 8, fontWeight: 700 }}>
        Save
      </button>
    </span>
  );
}

export default function WatchlistTab({ watchlist, investments, scannedStocks, onToggleWL, onStockClick, onAddInvestment, onRemoveInvestment, onCloseTrade, onSetInvestedAmount }) {
  const symbols = Array.from(new Set([...(watchlist || []), ...Object.keys(investments || {})]));

  const tradeRows = Object.entries(investments || {}).map(([sym, inv]) =>
    buildTradeRow(sym, inv, scannedStocks?.find((st) => st.symbol === sym))
  );
  const stats = computeTradeStats(tradeRows);

  return (
    <div>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900, fontSize: 22, letterSpacing: 1, background: `linear-gradient(90deg, ${YL}, ${BL})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          WATCHLIST
        </div>
        <div style={{ fontSize: 8, color: TX2, letterSpacing: 3, marginTop: 4 }}>{symbols.length} STOCK{symbols.length === 1 ? "" : "S"} TRACKED</div>
        <div style={{ height: 1, background: `linear-gradient(90deg, ${YL}40, transparent)`, marginTop: 8 }} />
      </motion.div>

      {tradeRows.length > 0 && <TradeStatsPanel stats={stats} />}

      {symbols.length === 0 ? (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
          className="ts-panel"
          style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 10, padding: 44, textAlign: "center", color: TX2 }}>
          <div style={{ fontSize: 12, color: TX, marginBottom: 5 }}>Watchlist empty</div>
          <div style={{ fontSize: 9 }}>Hit ☆ on any stock, or add an entry price here once you've scanned, to start tracking it.</div>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}
          className="ts-panel"
          style={{ background: CARD, border: `1px solid ${BD}`, borderRadius: 10, overflow: "hidden" }}>
          {symbols.map((sym, i) => {
            const s = scannedStocks?.find((st) => st.symbol === sym);
            const inv = investments?.[sym];
            const advice = inv && s ? getInvestmentAdvice(inv.entryPrice, s.price, s) : null;
            const verdictColor = advice ? (advice.verdict === "SELL" ? RD : GR) : TX2;
            const isWatchlisted = (watchlist || []).includes(sym);

            return (
              <motion.div
                key={sym}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.22, delay: Math.min(i * 0.045, 0.4), ease: "easeOut" }}
                className={s ? "hov-row" : ""}
                onClick={() => s && onStockClick(s)}
                style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 14px", borderBottom: `1px solid ${BD}`, borderLeft: s ? `3px solid ${scoreColor(s.score)}` : `3px solid ${BD}`, flexWrap: "wrap" }}
              >
                <div style={{ fontWeight: 700, fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: TX, minWidth: 58 }}>{sym}</div>

                {s ? (
                  <>
                    <div style={{ minWidth: 90, fontSize: 8, color: TX2 }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: isNum(s.price) ? TX : TX2, minWidth: 64 }}>{fmtPrice(s.price)}</div>
                    <div style={{ fontSize: 10, color: !isNum(s.change) ? TX2 : s.change >= 0 ? GR : RD, fontWeight: 600, minWidth: 58 }}>{fmtPct(s.change)}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: scoreColor(s.score), fontFamily: "'Space Grotesk',sans-serif", minWidth: 28 }}>{s.score}</div>
                    <div style={{ fontSize: 8, color: biasColor(s.tomorrowBias), minWidth: 56 }}>{s.technicalBias ?? s.tomorrowBias}</div>
                  </>
                ) : <div style={{ flex: 1, fontSize: 8, color: TX2 }}>Run a scan to see live data</div>}

                <div style={{ flex: 1, minWidth: 0 }} />

                {inv ? (
                  <>
                    {(() => {
                      const row = tradeRows.find((r) => r.symbol === sym);
                      const closed = row?.status === "closed";
                      const pnl = closed ? row.realizedPct : row?.unrealizedPct;
                      return (
                        <>
                          <div style={{ fontSize: 8, color: TX2, minWidth: 128, lineHeight: 1.5 }}>
                            <div>entry {fmtPrice(row?.entryPrice)} · {fmtDate(row?.entryDate)}</div>
                            {/* Only shown when an amount was recorded — a
                                position tracked by price alone has no cash
                                figures, and inventing them would be worse. */}
                            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                              {isNum(row?.amountInvested)
                                ? <span>invested {fmtMoney(row.amountInvested)} · {fmtShares(row.shares)}</span>
                                : <span style={{ color: TX2 }}>no amount recorded</span>}
                              {onSetInvestedAmount && (
                                <EditAmount symbol={sym} current={row?.amountInvested} onSave={onSetInvestedAmount} />
                              )}
                            </div>
                            <div>
                              stop {fmtPrice(row?.stopPrice)} · target {fmtPrice(row?.targetPrice)}
                            </div>
                            <div>
                              score {isNum(row?.entryScore) ? row.entryScore : NA} → {isNum(row?.currentScore) ? row.currentScore : NA}
                              {isNum(row?.entryScore) && isNum(row?.currentScore) && row.currentScore < row.entryScore - 15 && (
                                <span style={{ color: RD }}> (setup weakening)</span>
                              )}
                            </div>
                          </div>
                          <div style={{ minWidth: 88, whiteSpace: "nowrap" }}>
                            {/* Dollars lead when known — that is the figure
                                being asked for; the percentage stays beneath
                                it because it is what compares one trade to
                                another. */}
                            {isNum(row?.pnlAmount) ? (
                              <>
                                <div style={{ fontSize: 12, fontWeight: 800, color: row.pnlAmount >= 0 ? GR : RD, fontFamily: "'Space Grotesk',sans-serif" }}>
                                  {fmtMoneySigned(row.pnlAmount)}
                                </div>
                                <div style={{ fontSize: 8, fontWeight: 700, color: row.pnlAmount >= 0 ? GR : RD }}>
                                  {fmtPct(pnl, 1)} · now {fmtMoney(row.currentValue)}
                                </div>
                              </>
                            ) : (
                              <div style={{ fontSize: 10, fontWeight: 700, color: !isNum(pnl) ? TX2 : pnl >= 0 ? GR : RD }}>
                                {isNum(pnl) ? fmtPct(pnl, 1) : NA}
                              </div>
                            )}
                            <div style={{ fontSize: 7, color: TX2 }}>
                              {closed ? `realized · exit ${fmtPrice(row.exitPrice)}` : "unrealized"}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                    {(() => {
                      const row = tradeRows.find((r) => r.symbol === sym);
                      if (row?.status === "closed") {
                        return (
                          <div style={{ fontSize: 8, fontWeight: 800, color: TX2, background: CARD2, border: `1px solid ${BD}`, borderRadius: 5, padding: "3px 7px", whiteSpace: "nowrap" }}>
                            CLOSED
                          </div>
                        );
                      }
                      return advice ? (
                        <div style={{ fontSize: 8, fontWeight: 800, color: verdictColor, background: verdictColor + "15", border: `1px solid ${verdictColor}40`, borderRadius: 5, padding: "3px 7px", whiteSpace: "nowrap" }}>
                          {advice.verdict}
                        </div>
                      ) : (
                        <div style={{ fontSize: 8, color: TX2, whiteSpace: "nowrap" }}>scan for verdict</div>
                      );
                    })()}
                    {tradeRows.find((r) => r.symbol === sym)?.status === "open" && onCloseTrade && (
                      <CloseTrade symbol={sym} onClose={onCloseTrade} />
                    )}
                    <span onClick={(e) => { e.stopPropagation(); onRemoveInvestment(sym); }} style={{ fontSize: 10, color: TX2, cursor: "pointer", padding: "0 2px" }} title="Stop tracking">✕</span>
                  </>
                ) : s ? (
                  <QuickTrack symbol={sym} onAdd={onAddInvestment} />
                ) : (
                  <div style={{ fontSize: 8, color: TX2 }}>scan to add entry price</div>
                )}

                <span className="star" onClick={(e) => { e.stopPropagation(); onToggleWL(sym); }} style={{ fontSize: 12, color: isWatchlisted ? YL : TX2 }}>{isWatchlisted ? "★" : "☆"}</span>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
